import React, { useState, useEffect, useRef } from "react";
import { Link, graphql } from "gatsby";
import Seo from "../components/seo";
import AuthBlock from "../components/auth/AuthBlock";
import { useAuth } from "../context/AuthContext";
import WeatherForecast from "../components/WeatherForecast";
import { MapContainer, TileLayer, Marker, Popup, LayersControl, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { Helmet } from "react-helmet";

// Import ad images directly
import b1Image from "../images/ads/b1.png";
import b2Image from "../images/ads/b2.png";

// Utility function to split paragraphs intelligently
const formatParagraph = (paragraph) => {
  if (!paragraph) return '';
  
  // If the paragraph is already short, don't split it
  if (paragraph.length < 400) return paragraph;
  
  // Find sentences by looking for periods followed by a space
  const sentences = paragraph.match(/[^.!?]+[.!?]+\s/g) || [];
  
  // If we can't split by sentences, just return the original
  if (sentences.length <= 3) return paragraph;
  
  // Find a good breaking point - around halfway through the paragraph
  const midpoint = Math.floor(sentences.length / 2);
  
  // Create the two paragraphs
  const firstPart = sentences.slice(0, midpoint).join('').trim();
  const secondPart = sentences.slice(midpoint).join('').trim();
  
  // Return the two paragraphs with a proper break
  return `${firstPart}<br/><br/>${secondPart}`;
};

// AdBanner component for displaying rotating ads
const AdBanner = () => {
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const ads = [
    { path: "b1.png", url: "mailto:hello@californiafishplants.com" },
    { path: "b2.png", url: "https://gearlist.com/californiafishplants" }
  ];
  
  // Map of image paths to imported images
  const adImages = {
    "b1.png": b1Image,
    "b2.png": b2Image
  };

  useEffect(() => {
    // Set up rotation interval
    const rotationInterval = setInterval(() => {
      setCurrentAdIndex(prevIndex => (prevIndex + 1) % ads.length);
    }, 5000);

    // Clean up on unmount
    return () => clearInterval(rotationInterval);
  }, [ads.length]);

  const currentAd = ads[currentAdIndex];
  const adImage = adImages[currentAd.path];

  return (
    <div className="ad-banner-container mx-auto" style={{ maxWidth: '900px', width: '75%', maxHeight: '100%', margin: '1rem auto' }}>
      <a href={currentAd.url} target="_blank" rel="noopener noreferrer">
        <img 
          src={adImage} 
          alt="California Fish Plants" 
          className="w-full h-auto rounded-lg"
          style={{ objectFit: 'contain' }}
        />
      </a>
      <style jsx="true">{`
        @media (max-width: 768px) {
          .ad-banner-container {
            width: 100% !important;
            max-width: 100% !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          .ad-banner-container img {
            border-radius: 0 !important;
          }
        }
      `}</style>
    </div>
  );
};

// MapResizer component to handle resize issues
const MapResizer = () => {
  const map = useMap();
  
  useEffect(() => {
    // Small delay to ensure the container is fully rendered
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 100);
    
    return () => clearTimeout(timer);
  }, [map]);
  
  return null;
};

// Custom component to set map reference
const SetMapRef = ({ mapRef }) => {
  const map = useMap();
  
  useEffect(() => {
    mapRef.current = map;
    
    // Force a resize after map reference is set
    setTimeout(() => {
      map.invalidateSize();
    }, 250);
  }, [map, mapRef]);
  
  return null;
};

// Custom zoom control component that uses the map instance directly
const ZoomControl = () => {
  const map = useMap();
  
  return (
    <div className="leaflet-control-zoom leaflet-bar leaflet-control" 
         style={{ position: 'absolute', bottom: '10px', right: '10px', zIndex: '1000' }}>
      <a 
        className="leaflet-control-zoom-in" 
        href="#" 
        title="Zoom in"
        role="button"
        aria-label="Zoom in"
        onClick={(e) => {
          e.preventDefault();
          map.zoomIn();
        }}
      >+</a>
      <a 
        className="leaflet-control-zoom-out" 
        href="#" 
        title="Zoom out"
        role="button"
        aria-label="Zoom out"
        onClick={(e) => {
          e.preventDefault();
          map.zoomOut();
        }}
      >−</a>
    </div>
  );
};

const GuideTemplate = ({ pageContext }) => {
  const [activeTimeframe, setActiveTimeframe] = useState("3 Months");
  const [showTrout, setShowTrout] = useState(true);
  const [showCatfish, setShowCatfish] = useState(true);
  const [showTroutMA, setShowTroutMA] = useState(true);
  const [showCatfishMA, setShowCatfishMA] = useState(true);
  const [Plot, setPlot] = useState(null);
  const [isBrowser, setIsBrowser] = useState(false);
  const mapRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Auth state is now handled by the AuthBlock component

  // Add auth related states
  const { user, loading, signInWithMagicLink } = useAuth();

  // Debug logging for zoom value
  console.log('Guide zoom value:', {
    rawZoom: pageContext.zoom,
    parsedZoom: parseInt(pageContext.zoom),
    fallbackZoom: parseInt(pageContext.zoom) || 12,
    fullPageContext: pageContext
  });

  // Destructure zoom along with other values
  const {
    newTitle,
    introParagraph,
    generalBackground,
    howToPlanYourTrip,
    speciesToCatchAndGear,
    conclusion,
    latitude,
    longitude,
    id,
    plants,
    historicalPlantData,
    section,
    zoom,  // Add zoom to destructuring
    seoTitle,
    metaDescription,
    slug,
    ogImage,
    publishDate,
    lastModifiedDate,
    author,
    weatherExempt,
    location,
    county,
    countyLocations: initialCountyLocations // Get county locations from context
  } = pageContext;

  // Parse zoom once and store it
  const zoomLevel = parseInt(zoom) || 12;

  // Debug all available fields to locate the proper location field
  console.log("All pageContext:", pageContext);
  console.log("Section specifically:", section);
  console.log("County value:", county);
  console.log("CSV Column fields:", {
    "column5_location": pageContext.water, 
    "column14_county": county,
    "column16_title": pageContext.title || newTitle,
    "all_fields": Object.keys(pageContext),
    "slug": slug
  });
  
  // Use the slug to determine the location name - safer approach
  let locationName;
  
  // Use general approach for all locations
  locationName = pageContext.water || 
                pageContext.location || 
                pageContext.title || 
                slug?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  // Convert latitude and longitude to numbers
  const lat = parseFloat(latitude);
  const lng = parseFloat(longitude);

  // Check if we have valid coordinates
  const hasValidCoordinates = !isNaN(lat) && !isNaN(lng);
  
  // Debug coordinates
  console.log('GuideTemplate coordinates:', { 
    latitude, 
    longitude, 
    parsed: { lat, lng }, 
    hasValidCoordinates 
  });

  const mapContainerStyle = {
    width: "100%",
    height: "400px",
    marginBottom: "2rem",
    borderRadius: "8px",
  };

  const center = {
    lat: lat,
    lng: lng,
  };

  // Create custom icon for the marker
  const [orangeFishIcon, setOrangeFishIcon] = useState(null);
  const [greyFishIcon, setGreyFishIcon] = useState(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Create custom icon with Leaflet - fixed aspect ratio by using size 32x32
      const customIcon = L.icon({
        iconUrl: 'https://assets.californiafishplants.com/assets/pins/orange.png',
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32],
        className: 'fish-marker-icon' // Add custom class for CSS targeting
      });
      
      // Create grey fish icon for other locations
      const greyIcon = L.icon({
        iconUrl: 'https://assets.californiafishplants.com/assets/pins/grey.png',
        iconSize: [28, 28], // Slightly smaller than the main orange marker
        iconAnchor: [14, 28],
        popupAnchor: [0, -28],
        className: 'fish-marker-icon'
      });
      
      setOrangeFishIcon(customIcon);
      setGreyFishIcon(greyIcon);
      setIsBrowser(true);
    }
  }, []);
  
  // Use useEffect to load Plotly only on the client-side
  useEffect(() => {
    // Dynamically import Plot only on the client side
    import('react-plotly.js').then(module => {
      setPlot(() => module.default);
    });
  }, []);

  // Use the countyLocations passed in from the build process
  const [countyLocations, setCountyLocations] = useState(initialCountyLocations || []);
  
  useEffect(() => {
    console.log('County locations from build:', {county, locationCount: initialCountyLocations?.length || 0});
    
    // Update state with the pre-built county locations from pageContext
    if (initialCountyLocations && initialCountyLocations.length > 0) {
      setCountyLocations(initialCountyLocations);
    }
  }, [county, initialCountyLocations]);

  const getTimeframedData = (data, timeframe) => {
    if (!data) return [];
    const now = new Date();
    const months = timeframe === "3 Months" ? 3 : 
                  timeframe === "6 Months" ? 6 : 12;
    const cutoff = new Date(now.setMonth(now.getMonth() - months));
    
    return data.filter(item => new Date(item.weekOf) >= cutoff)
               .map(item => ({
                 ...item,
                 // Ensure these fields are boolean values
                 mtLassen: item.mtLassen === 'Y' || item.mtLassen === true,
                 lightning: item.lightning === 'Y' || item.lightning === true,
                 trophy: item.trophy === 'Y' || item.trophy === true
               }));
  };

  const calculateSMA = (data, windowSize = 4) => {
    const sma = [];
    for (let i = 0; i < data.length; i++) {
      if (i < windowSize - 1) {
        sma.push(null);  // First 3 points will be null for 4-week MA
      } else {
        let sum = 0;
        for (let j = 0; j < windowSize; j++) {
          sum += data[i - j];
        }
        sma.push(parseFloat((sum / windowSize).toFixed(2)));
      }
    }
    return sma;
  };

  const renderPlot = () => {
    // Return null during SSR
    if (!isBrowser || !Plot || !historicalPlantData?.length) return null;

    const data = getTimeframedData(historicalPlantData, activeTimeframe);
    
    // Debug log to see the data
    console.log("Historical Plant Data with flags:", data);

    // Always generate the full range of dates
    const now = new Date();
    const weeks = activeTimeframe === "3 Months" ? 13 : 
                 activeTimeframe === "6 Months" ? 26 : 52;
    
    const dates = [];
    for (let i = 0; i < weeks; i++) {
      const date = new Date(now);
      date.setDate(date.getDate() - (i * 7));
      // Get Sunday of that week
      const day = date.getDay();
      const diff = date.getDate() - day;
      date.setDate(diff);
      dates.unshift(date.toLocaleDateString('en-US'));
    }

    // Create an aggregated map of data by weekOf
    const aggregatedDataMap = {};
    
    // Process and aggregate all data points with the same weekOf
    data.forEach(item => {
      if (!aggregatedDataMap[item.weekOf]) {
        // Initialize entry for this week
        aggregatedDataMap[item.weekOf] = {
          weekOf: item.weekOf,
          troutAmount: 0,
          catfishAmount: 0,
          mtLassen: item.mtLassen,
          lightning: item.lightning,
          trophy: item.trophy,
          plantAmount: item.plantAmount
        };
      }
      
      // Add amounts from this entry to the aggregated total
      aggregatedDataMap[item.weekOf].troutAmount += item.troutAmount || 0;
      aggregatedDataMap[item.weekOf].catfishAmount += item.catfishAmount || 0;
      
      // Update flags if they're true in any entry
      aggregatedDataMap[item.weekOf].mtLassen = aggregatedDataMap[item.weekOf].mtLassen || item.mtLassen;
      aggregatedDataMap[item.weekOf].lightning = aggregatedDataMap[item.weekOf].lightning || item.lightning;
      aggregatedDataMap[item.weekOf].trophy = aggregatedDataMap[item.weekOf].trophy || item.trophy;
    });
    
    // Convert the aggregated data map to an array
    const aggregatedData = Object.values(aggregatedDataMap);
    
    // Merge generated dates with aggregated data
    const sortedData = dates.map(date => {
      const existingData = aggregatedDataMap[date];
      return existingData || {
        weekOf: date,
        troutAmount: 0,
        catfishAmount: 0,
        troutMA: null,
        catfishMA: null
      };
    });

    // Debug log to see sorted data
    console.log("Sorted data with flags:", sortedData);

    // Calculate moving averages
    const troutValues = sortedData.map(d => d.troutAmount || 0);
    const catfishValues = sortedData.map(d => d.catfishAmount || 0);
    
    const troutMA = calculateSMA(troutValues);
    const catfishMA = calculateSMA(catfishValues);

    // Update the data with calculated MAs
    sortedData.forEach((d, i) => {
      d.troutMA = troutMA[i];
      d.catfishMA = catfishMA[i];
    });

    const traces = [];

    if (showTrout) {
      traces.push({
        x: sortedData.map(d => d.weekOf),
        y: sortedData.map(d => d.troutAmount),
        type: 'bar',
        name: 'Trout',
        marker: { color: '#1bb0b0' },
        text: sortedData.map(d => {
          if (!d.plantAmount) return null;
          
          // Extract only specific emojis from plant amount
          const emojis = [];
          
          // Check for each emoji in the plantAmount string
          if (d.plantAmount.includes('🐟')) emojis.push('🐟');
          if (d.plantAmount.includes('⚡')) emojis.push('⚡');
          if (d.plantAmount.includes('🏆')) emojis.push('🏆');
          
          // If using boolean flags also works, add them as a fallback
          if (emojis.length === 0) {
            if (d.mtLassen === true) emojis.push('🐟');
            if (d.lightning === true) emojis.push('⚡');
            if (d.trophy === true) emojis.push('🏆');
          }
          
          // Return emojis with line breaks, or null if no emojis
          return emojis.length > 0 ? emojis.join('<br>') : null;
        }),
        textposition: 'outside',
        textfont: {
          size: 16
        },
        cliponaxis: false,
        hovertemplate: sortedData.map(d => {
          return d.plantAmount ? `${d.plantAmount}<extra></extra>` : `Trout: ${d.troutAmount} lbs<extra></extra>`;
        }),
        textangle: 0,  // Ensure text is not rotated
        mode: 'text+markers'
      });
    }

    if (showCatfish) {
      traces.push({
        x: sortedData.map(d => d.weekOf),
        y: sortedData.map(d => d.catfishAmount),
        type: 'bar',
        name: 'Catfish',
        marker: { color: '#ea6f63' },
        hovertemplate: sortedData.map(d => {
          return d.plantAmount ? `${d.plantAmount}<extra></extra>` : `Catfish: ${d.catfishAmount} lbs<extra></extra>`;
        })
      });
    }

    if (showTroutMA) {
      traces.push({
        x: sortedData.map(d => d.weekOf),
        y: troutMA,
        type: 'scatter',
        mode: 'lines',
        name: 'Trout MA',
        line: { 
          color: '#1bb0b0', 
          width: 1, 
          dash: 'dot',
          shape: 'spline',
          smoothing: 1.3
        },
        connectgaps: true,
        hovertemplate: 'Trout MA: %{y} lbs<extra></extra>'
      });
    }

    if (showCatfishMA) {
      traces.push({
        x: sortedData.map(d => d.weekOf),
        y: catfishMA,
        type: 'scatter',
        mode: 'lines',
        name: 'Catfish MA',
        line: { 
          color: '#ea6f63', 
          width: 1, 
          dash: 'dot',
          shape: 'spline',
          smoothing: 1.3
        },
        connectgaps: true,
        hovertemplate: 'Catfish MA: %{y} lbs<extra></extra>'
      });
    }

    // Compute maximum bar value among all data points
    const allValues = sortedData.reduce((acc, d) => {
      if (showTrout) acc.push(d.troutAmount || 0);
      if (showCatfish) acc.push(d.catfishAmount || 0);
      return acc;
    }, []);
    
    const maxBar = Math.max(...allValues, 0);
    const maxY = maxBar <= 0 ? 500 : maxBar * 1.1;
    const dtickVal = maxBar <= 0 ? 100 : null;

    const layout = {
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      font: { color: '#d6e1ec' },
      margin: { 
        t: 40,    // Increased top margin for symbols
        r: 20,
        b: 80,
        l: 60
      },
      barmode: 'group',
      showlegend: false,
      xaxis: {
        gridcolor: '#293c4f',
        tickangle: 45,
        tickfont: { size: 10 },
        automargin: true,
        type: 'category',
        tickmode: activeTimeframe === "1 Year" ? "array" : "auto",
        ...(activeTimeframe === "1 Year" && {
          tickvals: sortedData.filter((_, i) => i % 2 === 0).map(d => d.weekOf),
          ticktext: sortedData.filter((_, i) => i % 2 === 0).map(d => d.weekOf)
        }),
        showline: true,
        linecolor: '#d6e1ec',
        linewidth: 1,
        mirror: false,
        fixedrange: true // Disable zooming on x axis
      },
      yaxis: {
        title: 'Amount (lbs)',
        gridcolor: '#293c4f',
        zeroline: false,
        range: [0, maxY],
        tick0: 0,
        ...(dtickVal && { dtick: dtickVal }),
        showline: true,
        linecolor: '#d6e1ec',
        linewidth: 1,
        fixedrange: true // Disable zooming on y axis
      },
      hoverlabel: {
        font: { color: '#d6e1ec', size: 16 },
        bgcolor: 'rgba(20, 83, 116, 0.9)',
        bordercolor: '#1bb0b0'
      }
    };

    const config = { 
      responsive: true, 
      displayModeBar: false,
      scrollZoom: false,
      staticPlot: false
    };

    return (
      <Plot
        data={traces}
        layout={layout}
        config={config}
        style={{ width: '100%', height: '400px' }}
      />
    );
  };

  const renderHistoricalPlantSection = () => {
    if (plants !== "Y" || !historicalPlantData) return null;

    return (
      <div style={{ 
        marginBottom: '2rem',
        backgroundColor: '#001a33',
        padding: '2rem',
        borderRadius: '8px',
        position: 'relative', /* Ensure the section has position relative */
        zIndex: 1 /* Ensure a stacking context with low z-index */
      }}>
        <h2 style={{ marginBottom: '1rem', color: '#d6e1ec', paddingLeft: '16px', paddingRight: '16px' }}>Historical Fish Plants</h2>
        
        {isBrowser && (
          <>
            <div className="button-container">
              {["3 Months", "6 Months", "1 Year"].map(timeframe => (
                <button
                  key={timeframe}
                  className={`time-button ${activeTimeframe === timeframe ? 'active' : ''}`}
                  onClick={() => setActiveTimeframe(timeframe)}
                >
                  {timeframe}
                </button>
              ))}
            </div>

            <div className="toggle-container">
              <label className="toggle-item">
                <input
                  type="checkbox"
                  checked={showTrout}
                  onChange={() => setShowTrout(!showTrout)}
                />
                <div className="color-box trout-color"></div>
                Trout
              </label>
              <label className="toggle-item">
                <input
                  type="checkbox"
                  checked={showTroutMA}
                  onChange={() => setShowTroutMA(!showTroutMA)}
                />
                <div className="color-box trout-color"></div>
                Trout MA
              </label>
              <label className="toggle-item">
                <input
                  type="checkbox"
                  checked={showCatfish}
                  onChange={() => setShowCatfish(!showCatfish)}
                />
                <div className="color-box catfish-color"></div>
                Catfish
              </label>
              <label className="toggle-item">
                <input
                  type="checkbox"
                  checked={showCatfishMA}
                  onChange={() => setShowCatfishMA(!showCatfishMA)}
                />
                <div className="color-box catfish-color"></div>
                Catfish MA
              </label>
            </div>

            <div style={{ position: 'relative', overflow: 'hidden' }}>
              {renderPlot()}
              {!user && !loading && localStorage.getItem("fishMapTempAccess") !== "true" && (
                <AuthBlock
                  type="overlay"
                  title="Login Required"
                  message="You need to be logged in to access the complete historical fish plant data."
                  data-button-text="LOG IN / SIGN UP"
                  buttonText="LOG IN / SIGN UP"
                  loginButtonText="LOG IN / SIGN UP"
                  customButtonLabel="LOG IN / SIGN UP"
                  className="auth-button-override"
                  redirectToLogin={true}
                  allowAdminOverride={false}
                  onAuthenticated={() => window.location.reload()}
                />
              )}
            </div>
            
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '2rem',
              margin: '2rem 0',
              color: '#d6e1ec',
              fontSize: '0.9rem'
            }}>
              <div>Mt Lassen 🐟</div>
              <div>Lightning ⚡</div>
              <div>Trophy 🏆</div>
            </div>
            
            {/* Ad Banner 2: Between chart and table */}
            <AdBanner />
             
            <div className="table-container">
              <h3 style={{ color: '#d6e1ec', marginBottom: '1rem', paddingLeft: '16px', paddingRight: '16px' }}>Latest Plants</h3>
              <table className="last-plant-table">
                <thead>
                  <tr>
                    <th>Week of</th>
                    <th>Plant Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {historicalPlantData.slice(0, 10).map((plant, index) => (
                    <tr key={index}>
                      <td>{plant.weekOf}</td>
                      <td>{plant.plantAmount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    );
  };

  // Load Geist Mono font and force its application
  useEffect(() => {
    if (typeof document !== 'undefined') {
      // Add multiple sources for the Geist Mono font
      const fontCss = `
        @font-face {
          font-family: 'Geist Mono';
          src: url('https://fonts.cdnfonts.com/s/98372/GeistMono-Regular.woff2') format('woff2'),
               url('https://fonts.cdnfonts.com/s/98372/GeistMono-Regular.woff') format('woff');
          font-weight: 400;
          font-style: normal;
          font-display: block;
        }
      `;
      
      // Add the font-face declaration
      const fontStyle = document.createElement('style');
      fontStyle.textContent = fontCss;
      document.head.appendChild(fontStyle);
      
      // Add link to Geist Mono font
      const fontLink = document.createElement('link');
      fontLink.rel = 'stylesheet';
      fontLink.href = 'https://fonts.cdnfonts.com/css/geist-mono';
      document.head.appendChild(fontLink);
      
      // Add styles to ensure Geist Mono is applied to inputs and buttons
      const styleTag = document.createElement('style');
      styleTag.innerHTML = `
        /* CRITICAL: Force Geist Mono on button text */
        .auth-geist-mono-button * {
          font-family: 'Geist Mono', monospace !important;
          text-transform: uppercase !important;
          letter-spacing: 0.025em !important;
        }
        
        /* Force Geist Mono on input placeholder */
        input.auth-geist-mono::placeholder {
          font-family: 'Geist Mono', monospace !important;
          letter-spacing: 0.025em !important;
        }
      `;
      document.head.appendChild(styleTag);
      
      // Super aggressive font application
      const forceGeistMono = () => {
        document.querySelectorAll('.auth-geist-mono-button').forEach(button => {
          // Replace the button's inner HTML with a strongly styled span
          const buttonText = button.textContent.includes('...') ? '...' : 'LOG IN';
          button.innerHTML = '';
          
          const strongSpan = document.createElement('strong');
          strongSpan.style.cssText = 'font-family: "Geist Mono", monospace !important; text-transform: uppercase !important; letter-spacing: 0.025em !important;';
          strongSpan.textContent = buttonText;
          
          button.appendChild(strongSpan);
          
          // Also apply the style directly to the button
          button.style.fontFamily = '"Geist Mono", monospace !important';
          button.style.textTransform = 'uppercase !important';
        });
      };
      
      // Run the font forcing multiple times
      setTimeout(forceGeistMono, 0);
      setTimeout(forceGeistMono, 100);
      setTimeout(forceGeistMono, 500);
      
      // Clean up
      return () => {
        document.head.removeChild(styleTag);
        document.head.removeChild(fontLink);
        document.head.removeChild(fontStyle);
      };
    }
  }, []);

  // Use a direct DOM injection method to ensure the sidebar shows
  useEffect(() => {
    if (typeof document !== 'undefined' && county) {
      // Wait for the sidebar container to be available
      // REMOVED: This function was causing duplicate county locations in the sidebar
      // const checkAndInsertContent = () => {
      //   const sidebar = document.querySelector('.map-sidebar');
      //   if (sidebar) {
      //     console.log('Found sidebar element, injecting content');
          
      //     // Check if the county section already exists
      //     if (!document.querySelector('.county-section-injected')) {
      //       const locationsToUse = hardcodedCountyLocations[county] || [];
            
      //       if (locationsToUse.length > 0) {
      //         // Create section element
      //         const sectionDiv = document.createElement('div');
      //         sectionDiv.className = 'map-sidebar-section county-section-injected';
              
      //         // Create heading
      //         const heading = document.createElement('h3');
      //         heading.textContent = `Other Locations in ${county} County`;
      //         sectionDiv.appendChild(heading);
              
      //         // Create locations list
      //         const listDiv = document.createElement('div');
      //         listDiv.className = 'county-locations-list';
              
      //         // Add location links
      //         locationsToUse.forEach(item => {
      //           const link = document.createElement('a');
      //           link.href = item.permalink;
      //           link.textContent = item.location;
      //           link.style.display = 'block';
      //           link.style.color = '#a0aec0';
      //           link.style.textDecoration = 'none';
      //           link.style.padding = '6px 0';
      //           link.style.borderBottom = '1px solid rgba(255, 255, 255, 0.1)';
      //           link.style.fontSize = '14px';
      //           link.style.transition = 'color 0.2s ease';
              
      //           link.addEventListener('mouseover', () => {
      //             link.style.color = '#d6e1ec';
      //           });
      //           link.addEventListener('mouseout', () => {
      //             link.style.color = '#a0aec0';
      //           });
              
      //           listDiv.appendChild(link);
      //         });
              
      //         sectionDiv.appendChild(listDiv);
      //         sidebar.appendChild(sectionDiv);
              
      //         console.log(`Injected ${locationsToUse.length} county locations into sidebar`);
      //       }
      //     }
      //   } else {
      //     console.log('Sidebar element not found yet, will retry');
      //     setTimeout(checkAndInsertContent, 500);
      //   }
      // };
      
      // REMOVED: Don't start the injection process
      // setTimeout(checkAndInsertContent, 1000);
    }
  }, [county, isBrowser]);

  return (
    <>
      <Helmet>
        <meta charSet="UTF-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1.0" />
        {/* Leaflet CSS fixes for marker icon rendering */}
        <style>{`
          /* Override parent container constraints */
          .content, 
          .content-container,
          .page-container,
          main {
            overflow: visible !important;
            max-width: none !important; /* Remove any max-width constraints */
          }
          
          /* Make the map-wrapper full width by fixing parent containers */
          body .content-container {
            max-width: none !important;
            width: 100% !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          
          /* Fix for stretched markers */
          .fish-marker-icon {
            display: block !important;
          }
          
          .fish-marker-icon img {
            width: 32px !important;
            height: 32px !important;
            object-fit: contain !important;
          }
          
          /* Fix for Leaflet default icon image behavior */
          .leaflet-div-icon {
            background: transparent;
            border: none;
          }
          
          /* Prevent marker stretching */
          .leaflet-marker-icon {
            width: auto !important;
          }
          
          /* Override leaflet map container styles */
          .leaflet-container {
            width: 100% !important;
            height: 600px !important;
            z-index: 1;
            background-color: #1a202c;
            border-radius: 8px !important;
          }
        `}</style>
      </Helmet>
      
      <Seo 
        title={seoTitle || newTitle}
        description={metaDescription || introParagraph?.substring(0, 160)}
        path={`/guides/${slug}`}
        image={ogImage}
        articleData={{
          title: seoTitle || newTitle,
          publishDate: publishDate,
          modifiedDate: lastModifiedDate,
          author: author
        }}
      />
      
      <style>
        {`
          /* Basic Guide Styles */
          .button-container {
            display: flex;
            justify-content: center;
            border: 1px solid #d6e1ec;
            border-radius: 5px;
            margin: 0 auto 20px;
            overflow: hidden;
            width: fit-content;
            margin-left: auto;
            margin-right: auto;
          }

          h1, h2, h3 {
            font-family: 'Geist', sans-serif;
          }
          
          /* Required for map to break out of container */
          .content-wrapper {
            overflow: visible !important;
            position: relative;
            width: 100%;
          }
          
          /* Map container using the fish-plant-viewer width (max 1200px for wider maps) */
          .map-wrapper {
            width: 100% !important;
            max-width: 1200px !important;
            margin: 0 auto !important;
            position: relative !important;
            margin-bottom: 3rem !important; /* Add more space after map */
            transform: none !important;
            left: auto !important;
            box-sizing: border-box !important;
            z-index: 1 !important;
          }
          
          /* Force the map wrapper to be the right size with important rules */
          body .map-wrapper {
            width: 100% !important;
            max-width: 1200px !important;
            left: auto !important;
            transform: none !important;
            margin-left: auto !important;
            margin-right: auto !important;
          }
          
          /* Remove padding on mobile/tablet */
          @media (max-width: 991px) {
            .map-wrapper {
              padding-left: 0 !important;
              padding-right: 0 !important;
            }
            .content, .content-container {
              padding-left: 0 !important;
              padding-right: 0 !important;
            }
          }
          
          /* Map area container */
          .map-area {
            width: 100% !important;
            height: 600px !important;
            position: relative !important;
            z-index: 1 !important;
            border-radius: 8px !important;
            overflow: hidden !important;
          }
          
          /* Helper for title positioning */
          .title-wrapper {
            max-width: 800px;
            margin: 0 auto 1rem;
          }
          
          /* Time button styles for historical plants section */
          .time-button {
            background-color: #145374;
            border: none;
            color: #d6e1ec;
            padding: 10px 20px;
            font-size: 14px;
            cursor: pointer;
            transition: background-color 0.2s;
          }

          .time-button:hover {
            background-color: #1bb0b0;
          }

          .time-button.active {
            background-color: #1bb0b0;
          }
          
          /* Toggle items for the historical data section */
          .toggle-container {
            display: flex;
            justify-content: center;
            flex-wrap: wrap;
            gap: 20px;
            margin: 20px 0;
          }
          
          .toggle-item {
            display: flex;
            align-items: center;
            gap: 8px;
            color: #d6e1ec;
            cursor: pointer;
          }
          
          .toggle-item input[type="checkbox"] {
            cursor: pointer;
          }
          
          .color-box {
            width: 16px;
            height: 16px;
            border-radius: 3px;
          }
          
          .trout-color {
            background-color: #1bb0b0;
          }
          
          .catfish-color {
            background-color: #ea6f63;
          }
          
          /* Historical fish plants table styles */
          .table-container {
            margin-top: 30px;
            width: 100%;
            overflow-x: auto;
          }
          
          .last-plant-table {
            width: 100%;
            max-width: 600px;
            margin: 0 auto;
            border-collapse: collapse;
            background-color: rgba(20, 83, 116, 0.2);
            border-radius: 8px;
            overflow: hidden;
          }
          
          .last-plant-table th,
          .last-plant-table td {
            padding: 12px 16px;
            text-align: left;
            border-bottom: 1px solid rgba(214, 225, 236, 0.1);
          }
          
          .last-plant-table th {
            background-color: #145374;
            color: #d6e1ec;
            font-weight: 500;
          }
          
          .last-plant-table tr:last-child td {
            border-bottom: none;
          }
          
          .last-plant-table tbody tr:hover {
            background-color: rgba(27, 176, 176, 0.1);
          }
          
          @media (max-width: 600px) {
            .toggle-container {
              gap: 10px;
            }
            
            .time-button {
              padding: 8px 16px;
              font-size: 13px;
            }
            
            .last-plant-table th,
            .last-plant-table td {
              padding: 8px 12px;
              font-size: 14px;
            }
            
            .toggle-item {
              font-size: 14px;
            }
          }
          
          /* Plotly customizations */
          .js-plotly-plot .plotly .modebar {
            display: none !important;
          }
          
          .js-plotly-plot .plotly .main-svg {
            background: transparent !important;
          }
          
          /* Custom hover label styling */
          .js-plotly-plot .plotly .hovertext {
            background-color: rgba(20, 83, 116, 0.9) !important;
            border-radius: 4px !important;
            padding: 8px !important;
            color: #d6e1ec !important;
          }
          
          /* Hamburger menu styling */
          .hamburger-icon {
            position: absolute;
            top: 15px;
            left: 15px;
            width: 35px;
            height: 35px;
            background-color: rgba(0, 0, 0, 0.6);
            border-radius: 4px;
            display: flex;
            justify-content: center;
            align-items: center;
            cursor: pointer;
            z-index: 1000;
            transition: background-color 0.3s ease;
          }
          
          .hamburger-icon:hover {
            background-color: rgba(0, 0, 0, 0.8);
          }
          
          .hamburger-icon .bar {
            width: 20px;
            height: 2px;
            background-color: white;
            position: relative;
            transition: all 0.3s ease;
          }
          
          .hamburger-icon .bar::before,
          .hamburger-icon .bar::after {
            content: '';
            position: absolute;
            width: 20px;
            height: 2px;
            background-color: white;
            transition: all 0.3s ease;
          }
          
          .hamburger-icon .bar::before {
            transform: translateY(-6px);
          }
          
          .hamburger-icon .bar::after {
            transform: translateY(6px);
          }
          
          /* Animated hamburger to X */
          .hamburger-icon.open .bar {
            background-color: transparent;
          }
          
          .hamburger-icon.open .bar::before {
            transform: rotate(45deg);
          }
          
          .hamburger-icon.open .bar::after {
            transform: rotate(-45deg);
          }
          
          /* Sidebar styling */
          .map-sidebar-container {
            position: absolute;
            top: 0;
            left: 0;
            width: 0;
            height: 100%;
            z-index: 999;
            overflow: hidden;
            pointer-events: none;
            transition: width 0.3s ease;
          }
          
          .map-sidebar-container.open {
            width: 300px;
            pointer-events: auto;
          }
          
          @media (max-width: 480px) {
            .map-sidebar-container.open {
              width: 260px;
            }
          }
          
          
          .map-sidebar {
            position: absolute;
            top: 0;
            left: 0;
            width: 300px;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.6);
            transform: translateX(-100%);
            transition: transform 0.3s ease;
            display: flex;
            flex-direction: column;
            padding: 60px 15px 15px;
            overflow-y: auto;
            -ms-overflow-style: none;  /* Hide scrollbar for IE and Edge */
            scrollbar-width: none;     /* Hide scrollbar for Firefox */
            box-shadow: none;
            outline: none;
            border: none;
          }
          
          .map-sidebar-container.open .map-sidebar {
            transform: translateX(0);
          }
          
          /* Hide scrollbar for Chrome, Safari and Opera */
          .map-sidebar::-webkit-scrollbar {
            display: none;
            width: 0;
            background: transparent;
          }
          
          @media (max-width: 480px) {
            .map-sidebar {
              width: 260px;
            }
          }
          
          .map-sidebar h3 {
            color: white;
            margin-bottom: 15px;
            font-size: 18px;
            border-bottom: 1px solid rgba(255, 255, 255, 0.2);
            padding-bottom: 10px;
          }
          
          .map-sidebar-section {
            margin-bottom: 20px;
          }
          
          /* Click outside to close sidebar */
          .sidebar-backdrop {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.5);
            z-index: 998;
            display: none;
          }
          
          .sidebar-backdrop.visible {
            display: block;
          }
          
          /* Style the layer control and zoom buttons like OpenStreetMap dark theme */
          .leaflet-control-layers,
          .leaflet-bar a {
            background-color: rgba(0, 0, 0, 0.6) !important;
            color: white !important;
            border: 1px solid rgba(255, 255, 255, 0.2) !important;
          }
          
          .leaflet-control-layers-toggle {
            background-color: rgba(0, 0, 0, 0.6) !important;
          }
          
          .leaflet-control-layers-expanded {
            background-color: rgba(0, 0, 0, 0.8) !important;
            color: white !important;
          }
          
          .leaflet-control-layers-selector + span {
            color: white !important;
          }
          
          .leaflet-bar a:hover {
            background-color: rgba(0, 0, 0, 0.8) !important;
          }
          
          /* Hide default zoom control which appears on the left */
          .leaflet-top.leaflet-left {
            display: none !important;
          }
          
          /* Controls positioning and appearance */
          .leaflet-top.leaflet-right {
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            margin-top: 10px;
            margin-right: 10px;
          }
          
          /* Override default leaflet margins */
          .leaflet-control {
            clear: none !important;
          }
          
          /* Consistent styling for all controls */
          .leaflet-control-layers,
          .leaflet-control-zoom,
          .leaflet-control-layers-toggle,
          .leaflet-control-zoom-in,
          .leaflet-control-zoom-out {
            background-color: rgba(0, 0, 0, 0.6) !important;
            color: white !important;
            border: none !important;
            border-radius: 4px !important;
            box-shadow: none !important;
          }
          
          /* Remove default margin between controls */
          .leaflet-control-zoom {
            margin-top: 5px !important;
            margin-right: 0 !important;
          }
          
          .leaflet-control-layers {
            margin-right: 0 !important;
          }
          
          /* Make layer control close when clicking outside */
          .leaflet-control-layers-expanded {
            padding: 6px 10px 6px 6px;
            z-index: 1000;
            background-color: rgba(0, 0, 0, 0.8) !important;
            color: white !important;
            border-radius: 4px !important;
          }
          
          /* Fix zoom button contrast */
          .leaflet-control-zoom-in,
          .leaflet-control-zoom-out {
            line-height: 30px !important;
            text-indent: 0 !important;
            text-align: center !important;
            font-size: 16px !important;
          }
          
          /* Override hover states */
          .leaflet-control-layers:hover,
          .leaflet-control-zoom-in:hover,
          .leaflet-control-zoom-out:hover {
            background-color: rgba(26, 32, 44, 0.8) !important;
            color: #d6e1ec !important;
          }
          
          /* County locations list styling */
          .county-locations-list {
            max-height: 300px;
            overflow-y: auto;
            margin-top: 10px;
            padding-right: 5px;
          }
          
          .county-locations-list::-webkit-scrollbar {
            width: 5px;
          }
          
          .county-locations-list::-webkit-scrollbar-track {
            background: rgba(0, 0, 0, 0.2);
            border-radius: 5px;
          }
          
          .county-locations-list::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.2);
            border-radius: 5px;
          }
          
          .county-locations-list::-webkit-scrollbar-thumb:hover {
            background: rgba(255, 255, 255, 0.3);
          }
          
          .county-locations-list a:last-child {
            border-bottom: none;
          }
          
          .county-locations-list a:hover {
            color: #d6e1ec !important;
          }
          
          /* Make the attribution smaller */
          .leaflet-control-attribution {
            font-size: 8px !important;
            padding: 0 5px !important;
            background-color: rgba(255, 255, 255, 0.7) !important;
            color: #333 !important;
            backdrop-filter: blur(2px);
          }
          
          .leaflet-control-attribution a {
            color: #0078A8 !important;
            text-decoration: none !important;
          }
        `}
      </style>

      <div className="content-wrapper">
        <div style={{
          width: '100%',
          maxWidth: '800px',
          margin: '0 auto',
          padding: '2rem 0',
          position: 'relative',
          overflow: 'visible'
        }}>
          <div style={{ marginBottom: '2rem' }}>
            <Link 
              to="/guides"
              style={{
                display: 'inline-block',
                padding: '0.75rem 1.5rem',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: '#fff',
                textDecoration: 'none',
                borderRadius: '8px',
                transition: 'background-color 0.2s ease'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
            >
              Back to Guides
            </Link>
          </div>

          {/* Ad Banner 1: Top of the page above the first paragraph */}
          <AdBanner />

          <h1 style={{ color: '#d6e1ec', marginBottom: '0.5rem', paddingLeft: '16px', paddingRight: '16px' }}>{newTitle}</h1>
          {section && (
            <div style={{
              display: 'inline-block',
              backgroundColor: '#1bb0b0',
              color: '#fff',
              padding: '0.25rem 0.75rem',
              borderRadius: '999px',
              fontSize: '0.875rem',
              fontWeight: '500',
              marginBottom: '1.5rem',
              fontFamily: 'Geist Mono, monospace',
              marginLeft: '16px'
            }}>
              {section}
            </div>
          )}
          
          {/* Introduction */}
          <div style={{ marginBottom: '2rem' }}>
            <p style={{ color: '#fff', lineHeight: '1.6', paddingLeft: '16px', paddingRight: '16px' }} dangerouslySetInnerHTML={{ __html: formatParagraph(introParagraph) }}></p>
          </div>

          {/* Moved Historical Plant Section here */}
          {renderHistoricalPlantSection()}
        </div>
          
        {/* Map (if coordinates are valid) - Note: This breaks out of the content container */}
        {hasValidCoordinates && (
          <>
            <div className="title-wrapper">
              {/* Ad Banner 3: Above the location map */}
              <AdBanner />
              
              <h2 style={{ color: '#d6e1ec', paddingLeft: '16px', paddingRight: '16px' }}>Location</h2>
            </div>
            <div className="map-wrapper" style={{ 
              width: '100% !important', 
              maxWidth: '1200px !important',
              margin: '0 auto !important',
              position: 'relative !important',
              transform: 'none !important',
              left: 'auto !important',
              zIndex: '1 !important'
            }}>
              <div className="map-area">
                {/* Backdrop to close sidebar on mobile */}
                {isBrowser && sidebarOpen && (
                  <div 
                    className={`sidebar-backdrop ${sidebarOpen ? 'visible' : ''}`}
                    onClick={() => setSidebarOpen(false)}
                  />
                )}
                
                {/* Hamburger menu icon */}
                <div 
                  className={`hamburger-icon ${sidebarOpen ? 'open' : ''}`}
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                >
                  <div className="bar"></div>
                </div>
                
                {/* Sidebar container */}
                <div className={`map-sidebar-container ${sidebarOpen ? 'open' : ''}`}>
                  <div className="map-sidebar">
                    <div className="map-sidebar-section">
                      <h3>{locationName || slug?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || "This Location"}</h3>
                      {/* Main fishing spot with orange marker */}
                      <a 
                        style={{
                          display: 'block',
                          color: '#ffffff',
                          textDecoration: 'none',
                          padding: '8px 0',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                          fontSize: '14px',
                          marginBottom: '10px'
                        }}
                      >
                        <span 
                          style={{
                            display: 'inline-block',
                            marginRight: '8px',
                            width: '16px',
                            height: '16px',
                            background: 'url(https://assets.californiafishplants.com/assets/pins/orange.png) no-repeat center center',
                            backgroundSize: 'contain',
                            verticalAlign: 'middle'
                          }}
                        />
                        {locationName}
                      </a>
                    </div>
                    
                    {county && countyLocations.length > 0 && (
                      <div className="map-sidebar-section">
                        <h3>Other Locations in {county} County</h3>
                        <div className="county-locations-list">
                          {countyLocations.map((item, index) => (
                            <a 
                              key={index}
                              href={item.permalink}
                              target="_self"
                              style={{
                                display: 'block',
                                color: '#a0aec0',
                                textDecoration: 'none',
                                padding: '6px 0',
                                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                                fontSize: '14px',
                                transition: 'color 0.2s ease'
                              }}
                              onMouseOver={(e) => e.target.style.color = '#d6e1ec'}
                              onMouseOut={(e) => e.target.style.color = '#a0aec0'}
                            >
                              <span 
                                style={{
                                  display: 'inline-block',
                                  marginRight: '8px',
                                  width: '16px',
                                  height: '16px',
                                  background: 'url(https://assets.californiafishplants.com/assets/pins/grey.png) no-repeat center center',
                                  backgroundSize: 'contain',
                                  verticalAlign: 'middle'
                                }}
                              />
                              {item.location}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {isBrowser && (
                  <MapContainer 
                    center={[lat, lng]}
                    zoom={zoomLevel}
                    scrollWheelZoom={true}
                    zoomControl={false}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <MapResizer />
                    <SetMapRef mapRef={mapRef} />
                    <ZoomControl />
                    
                    <LayersControl position="topright" collapsed={true}>
                      <LayersControl.BaseLayer checked name="Humanitarian">
                        <TileLayer
                          url="https://tile-{s}.openstreetmap.fr/hot/{z}/{x}/{y}.png"
                          attribution='© <a href="https://openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="https://hot.openstreetmap.org/">HOT</a>'
                          maxZoom={19}
                        />
                      </LayersControl.BaseLayer>
                      <LayersControl.BaseLayer name="Standard">
                        <TileLayer
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          attribution='© <a href="https://openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                          maxZoom={19}
                        />
                      </LayersControl.BaseLayer>
                      <LayersControl.BaseLayer name="Satellite">
                        <TileLayer
                          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                          attribution='© <a href="https://www.esri.com/">Esri</a>'
                          maxZoom={19}
                        />
                      </LayersControl.BaseLayer>
                    </LayersControl>
                    
                    {orangeFishIcon && (
                      <Marker 
                        position={[lat, lng]} 
                        icon={orangeFishIcon}
                      >
                        <Popup>
                          <div className="popup-content">
                            <h3><strong>{locationName}</strong></h3>
                            {section && <p>Section: {section}</p>}
                          </div>
                        </Popup>
                      </Marker>
                    )}
                    
                    {/* Add county location markers with grey fish icon */}
                    {greyFishIcon && countyLocations && countyLocations.length > 0 && 
                      countyLocations.map((item, index) => {
                        // Skip the current location to avoid duplicate marker
                        if (item.location === locationName) {
                          return null;
                        }
                        
                        // Check if the item has valid coordinates
                        if (item.latitude && item.longitude) {
                          const itemLat = parseFloat(item.latitude);
                          const itemLng = parseFloat(item.longitude);
                          
                          // Only add marker if coordinates are valid
                          if (!isNaN(itemLat) && !isNaN(itemLng)) {
                            return (
                              <Marker
                                key={`county-location-${index}`}
                                position={[itemLat, itemLng]}
                                icon={greyFishIcon}
                              >
                                <Popup>
                                  <div className="popup-content">
                                    <h3><strong>{item.location}</strong></h3>
                                    <a 
                                      href={item.permalink}
                                      target="_self" 
                                      className="text-sm text-blue-400 hover:text-blue-300"
                                    >
                                      View Details
                                    </a>
                                  </div>
                                </Popup>
                              </Marker>
                            );
                          }
                        }
                        return null;
                      })
                    }
                  </MapContainer>
                )}
              </div>
            </div>
          </>
        )}
        
        <div style={{
          width: '100%',
          maxWidth: '800px',
          margin: '0 auto',
          padding: '0',
          position: 'relative'
        }}>
          {/* Ad Banner 4: Below map, above weather */}
          <AdBanner />
          
          {/* Weather Forecast */}
          {hasValidCoordinates && weatherExempt !== "Y" && (
            <>
              {console.log('Rendering WeatherForecast with coordinates:', { lat, lng })}
              <WeatherForecast latitude={lat} longitude={lng} />
            </>
          )}
          
          {/* General Background */}
          <div style={{ marginBottom: '2rem' }}>
            {/* Ad Banner 5: Above General Background */}
            <AdBanner />
            
            <h2 style={{ marginBottom: '1rem', color: '#d6e1ec', paddingLeft: '16px', paddingRight: '16px' }}>General Background</h2>
            <p style={{ color: '#fff', lineHeight: '1.6', paddingLeft: '16px', paddingRight: '16px' }} dangerouslySetInnerHTML={{ __html: formatParagraph(generalBackground) }}></p>
          </div>
          
          {/* How to Plan Your Trip */}
          <div style={{ marginBottom: '2rem' }}>
            <h2 style={{ marginBottom: '1rem', color: '#d6e1ec', paddingLeft: '16px', paddingRight: '16px' }}>How to Plan Your Trip</h2>
            <p style={{ color: '#fff', lineHeight: '1.6', paddingLeft: '16px', paddingRight: '16px' }} dangerouslySetInnerHTML={{ __html: formatParagraph(howToPlanYourTrip) }}></p>
          </div>
          
          {/* Species to Catch and Gear */}
          <div style={{ marginBottom: '2rem' }}>
            <h2 style={{ marginBottom: '1rem', color: '#d6e1ec', paddingLeft: '16px', paddingRight: '16px' }}>Species to Catch and Gear</h2>
            <p style={{ color: '#fff', lineHeight: '1.6', paddingLeft: '16px', paddingRight: '16px' }} dangerouslySetInnerHTML={{ __html: formatParagraph(speciesToCatchAndGear) }}></p>
          </div>
          
          {/* Final Notes for the Water */}
          <div style={{ marginBottom: '2rem' }}>
            {/* Ad Banner 6: Above Final Notes */}
            <AdBanner />
            
            <h2 style={{ marginBottom: '1rem', color: '#d6e1ec', paddingLeft: '16px', paddingRight: '16px' }}>Final Notes for the Water</h2>
            <p style={{ color: '#fff', lineHeight: '1.6', paddingLeft: '16px', paddingRight: '16px' }} dangerouslySetInnerHTML={{ __html: formatParagraph(conclusion) }}></p>
          </div>
        </div>
      </div>
    </>
  );
};

export default GuideTemplate;
